import * as React from 'react';
import moment from 'moment';
import { Button, Col, DatePicker, Row, Skeleton, Table } from 'antd';
import { GraphqlQuery } from 'requestapijs';
import { CompanyService } from '../../service/CompanyService';
import { downloadToFile } from '../helper';

type ReportKind = 'jobOrder' | 'purchaseOrder';

interface PendingOrderReportRow {
  rowId: string;
  vendorName: string;
  date: string;
  jobOrderNumber?: string;
  purchaseOrderNumber?: string;
  productName: string;
  jobOrderQuantity?: number;
  orderQuantity?: number;
  receivedQuantity: number;
  duesQuantity: number;
}

const queryFor = (kind: ReportKind) => {
  const field =
    kind === 'jobOrder'
      ? 'pendingJobOrderReport'
      : 'pendingPurchaseOrderReport';
  const orderNumber =
    kind === 'jobOrder' ? 'jobOrderNumber' : 'purchaseOrderNumber';
  const orderQuantity =
    kind === 'jobOrder' ? 'jobOrderQuantity' : 'orderQuantity';

  return `
    query PendingOrderReport($companyId: ID!, $startDate: Date!, $endDate: Date!) {
      ${field}(companyId: $companyId, startDate: $startDate, endDate: $endDate) {
        rowId
        vendorName
        date
        ${orderNumber}
        productName
        ${orderQuantity}
        receivedQuantity
        duesQuantity
      }
    }
  `;
};

export const PendingOrderReport: React.FunctionComponent<Props> = ({
  kind,
}) => {
  const [companyId, setCompanyId] = React.useState<string | null>(null);
  const [companyName, setCompanyName] = React.useState('');
  const [startDate, setStartDate] = React.useState(
    moment().format('YYYY-MM-DD'),
  );
  const [endDate, setEndDate] = React.useState(moment().format('YYYY-MM-DD'));
  const companyService = React.useMemo(() => new CompanyService(), []);

  React.useEffect(() => {
    const subscription = companyService.company$.subscribe((company) => {
      setCompanyId(company?.id || null);
      setCompanyName(company?.name || '');
    });
    return () => subscription.unsubscribe();
  }, [companyService]);

  if (!companyId) {
    return <Skeleton active />;
  }

  const orderNumberTitle =
    kind === 'jobOrder' ? 'Job Order Number' : 'Purchase Order Number';
  const orderQuantityTitle =
    kind === 'jobOrder' ? 'Job Order Quantity' : 'Purchase Order Quantity';
  const downloadCsv = (rows: PendingOrderReportRow[]) => {
    const headers = [
      'Vendor Name',
      'Date',
      orderNumberTitle,
      'Product Name',
      orderQuantityTitle,
      'Received Qty',
      'Dues Qty',
    ];
    const csvValue = (value: string | number | undefined) =>
      `"${String(value ?? '').replace(/"/g, '""')}"`;
    const orderNumber = (row: PendingOrderReportRow) =>
      kind === 'jobOrder' ? row.jobOrderNumber : row.purchaseOrderNumber;
    const quantity = (row: PendingOrderReportRow) =>
      kind === 'jobOrder' ? row.jobOrderQuantity : row.orderQuantity;
    const csv = [
      headers.map(csvValue).join(','),
      ...rows.map((row) =>
        [
          row.vendorName,
          row.date ? moment(row.date).format('DD-MM-YYYY') : '',
          orderNumber(row),
          row.productName,
          quantity(row),
          row.receivedQuantity,
          row.duesQuantity,
        ]
          .map(csvValue)
          .join(','),
      ),
    ].join('\n');

    downloadToFile(
      csv,
      `pending-${kind}-order-report-${startDate}-to-${endDate}`,
      `PENDING ${kind === 'jobOrder' ? 'JOB' : 'PURCHASE'} ORDER REPORT`,
      companyName,
      '',
      moment(startDate).format('DD-MM-YYYY'),
      moment(endDate).format('DD-MM-YYYY'),
      '',
    );
  };
  const columns = [
    { title: 'Vendor Name', dataIndex: 'vendorName', key: 'vendorName' },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (date: string) => (date ? moment(date).format('DD-MM-YYYY') : ''),
    },
    {
      title: orderNumberTitle,
      dataIndex: kind === 'jobOrder' ? 'jobOrderNumber' : 'purchaseOrderNumber',
      key: 'orderNumber',
    },
    { title: 'Product Name', dataIndex: 'productName', key: 'productName' },
    {
      title: orderQuantityTitle,
      dataIndex: kind === 'jobOrder' ? 'jobOrderQuantity' : 'orderQuantity',
      key: 'orderQuantity',
    },
    {
      title: 'Received Qty',
      dataIndex: 'receivedQuantity',
      key: 'receivedQuantity',
    },
    { title: 'Dues Qty', dataIndex: 'duesQuantity', key: 'duesQuantity' },
  ];

  return (
    <>
      <Row gutter={8} style={{ marginBottom: 12 }}>
        <Col span={5}>
          <DatePicker
            format="DD-MM-YYYY"
            placeholder="Start Date"
            value={moment(startDate)}
            allowClear={false}
            onChange={(date) =>
              date && setStartDate(moment(date).format('YYYY-MM-DD'))
            }
            disabledDate={(currentDate) =>
              !!currentDate && currentDate.valueOf() > moment(endDate).valueOf()
            }
            style={{ width: '100%' }}
          />
        </Col>
        <Col span={5}>
          <DatePicker
            format="DD-MM-YYYY"
            placeholder="End Date"
            value={moment(endDate)}
            allowClear={false}
            onChange={(date) =>
              date && setEndDate(moment(date).format('YYYY-MM-DD'))
            }
            disabledDate={(currentDate) =>
              !!currentDate &&
              currentDate.valueOf() < moment(startDate).valueOf()
            }
            style={{ width: '100%' }}
          />
        </Col>
      </Row>
      <GraphqlQuery
        queryString={queryFor(kind)}
        variables={{ companyId, startDate, endDate }}
        render={(
          response: { [key: string]: PendingOrderReportRow[] },
          error,
          loading,
        ) => {
          if (loading) return <Skeleton active />;
          if (error) return <div>Unable to load the pending order report.</div>;

          const field =
            kind === 'jobOrder'
              ? 'pendingJobOrderReport'
              : 'pendingPurchaseOrderReport';
          const rows = response?.[field] || [];
          const downloadButton = (
            <Button icon="download" onClick={() => downloadCsv(rows)} />
          );
          return (
            <Table
              dataSource={rows}
              columns={columns}
              rowKey="rowId"
              pagination={{
                pageSize: 50,
                showSizeChanger: true,
                showTotal: () => downloadButton,
              }}
              scroll={{ x: true }}
              locale={{
                emptyText: 'No pending order items for this date range.',
              }}
            />
          );
        }}
      />
    </>
  );
};

interface Props {
  kind: ReportKind;
}
